import { useState, useEffect, useCallback } from 'react'
import { Link } from 'react-router-dom'
import { api, Post, Connection } from '../api/client'
import { useWorkspaceContext } from '../hooks/useWorkspaceContext'
import MultiSelectDropdown from '../components/MultiSelectDropdown'

const STATUS_FILTERS = ['All', 'Draft', 'Scheduled', 'Publishing', 'Published', 'PartiallyPublished', 'Failed', 'Deleted'] as const

function getPlatformPostUrl(platformName?: string, platformPostID?: string, displayName?: string): string | null {
  if (!platformPostID || !platformName) return null
  switch (platformName) {
    case 'Bluesky': {
      if (!platformPostID.startsWith('at://')) return null
      const parts = platformPostID.replace('at://', '').split('/')
      if (parts.length < 3) return null
      return `https://bsky.app/profile/${parts[0]}/post/${parts[2]}`
    }
    case 'Facebook':
      return `https://www.facebook.com/${platformPostID}`
    case 'X/Twitter':
      return `https://x.com/i/status/${platformPostID}`
    case 'LinkedIn Org':
    case 'LinkedIn Personal':
      if (platformPostID.startsWith('urn:'))
        return `https://www.linkedin.com/feed/update/${platformPostID}/`
      return `https://www.linkedin.com/feed/update/urn:li:share:${platformPostID}/`
    case 'Pinterest':
      return `https://www.pinterest.com/pin/${platformPostID}/`
    case 'Threads': {
      if (!displayName) return null
      const handleMatch = displayName.match(/@([\w.]+)/)
      if (handleMatch) return `https://www.threads.net/@${handleMatch[1]}`
      return null
    }
    case 'Instagram': {
      if (!displayName) return null
      const igHandle = displayName.match(/@([\w.]+)/)
      if (igHandle) return `https://www.instagram.com/${igHandle[1]}/`
      return null
    }
    case 'TikTok': {
      if (!displayName) return null
      return `https://www.tiktok.com/@${displayName}`
    }
    default: return null
  }
}

export default function Posts() {
  const { activeWorkspace } = useWorkspaceContext()
  const wsId = activeWorkspace?.workspaceID ?? 0
  const [posts, setPosts] = useState<Post[]>([])
  const [filter, setFilter] = useState<string>('All')
  const [platformFilter, setPlatformFilter] = useState<string[]>([])
  const [connections, setConnections] = useState<Connection[]>([])
  const [loading, setLoading] = useState(true)
  const [searchText, setSearchText] = useState('')
  const [deletingTargets, setDeletingTargets] = useState<Set<number>>(new Set())
  const [modalMedia, setModalMedia] = useState<{ url: string; isVideo: boolean; index: number; all: Array<{ url: string; isVideo: boolean }> } | null>(null)
  const [editingCaption, setEditingCaption] = useState<{ postId: number; targetId: number; connectionId: number; platformPostID: string; currentText: string } | null>(null)
  const [editCaptionText, setEditCaptionText] = useState('')

  const openMediaModal = useCallback((url: string, isVideo: boolean, index: number, all: Array<{ url: string; isVideo: boolean }>) => {
    setModalMedia({ url, isVideo, index, all })
  }, [])

  const navigateModal = useCallback((dir: 1 | -1) => {
    setModalMedia(prev => {
      if (!prev) return null
      const newIndex = prev.index + dir
      if (newIndex < 0 || newIndex >= prev.all.length) return prev
      const item = prev.all[newIndex]
      return { url: item.url, isVideo: item.isVideo, index: newIndex, all: prev.all }
    })
  }, [])

  useEffect(() => {
    api.get<Connection[]>(`/workspaces/${wsId}/connections`).then(setConnections).catch(err => console.error('[SMM]', err))
  }, [])

  useEffect(() => {
    setLoading(true)
    const statusParam = filter === 'All' ? '' : `?status=${filter}`
    api.get<Post[]>(`/workspaces/${wsId}/posts${statusParam}`)
      .then(setPosts)
      .catch(err => console.error("[SMM]", err))
      .finally(() => setLoading(false))
  }, [filter])

  const platforms = [...new Set(connections.map(c => c.platformName).filter((p): p is string => !!p))]

  const filteredPosts = posts.filter(p => {
    if (platformFilter.length > 0 && p.targets && p.targets.length > 0) {
      if (!p.targets.some(t => t.platformName && platformFilter.includes(t.platformName))) return false
    }
    if (searchText) {
      const lower = searchText.toLowerCase()
      if (!p.contentText?.toLowerCase().includes(lower)) return false
    }
    return true
  })

  const handleDelete = async (postId: number) => {
    if (!confirm('Delete this post from the database? To remove from platforms, use the per-platform delete buttons.')) return
    await api.del(`/workspaces/${wsId}/posts/${postId}`)
    setPosts(prev => prev.filter(p => p.postID !== postId))
  }

  const handleDeleteFromPlatform = async (postId: number, targetId: number, displayName: string) => {
    if (!confirm(`Delete this post from ${displayName}? This will remove it from the platform.`)) return
    setDeletingTargets(prev => new Set(prev).add(targetId))
    try {
      const result = await api.del<{ post: Post; message: string; error?: string }>(
        `/workspaces/${wsId}/posts/${postId}/targets/${targetId}`)
      if (result.post) {
        setPosts(prev => prev.map(p => p.postID === postId ? result.post : p))
      }
      if (result.error) {
        console.error('[SMM] Delete from platform:', result.message)
      }
    } catch (err) {
      console.error('[SMM] Delete from platform failed:', err)
    } finally {
      setDeletingTargets(prev => { const s = new Set(prev); s.delete(targetId); return s })
    }
  }

  const handlePublish = async (postId: number) => {
    await api.post(`/workspaces/${wsId}/posts/${postId}/publish`)
    const updated = await api.get<Post>(`/workspaces/${wsId}/posts/${postId}`)
    setPosts(prev => prev.map(p => p.postID === postId ? updated : p))
  }

  const handleEditCaption = async () => {
    if (!editingCaption) return
    try {
      await api.put(`/workspaces/${wsId}/inbox/posts/${encodeURIComponent(editingCaption.platformPostID)}/caption?connectionId=${editingCaption.connectionId}`, { caption: editCaptionText })
      setPosts(prev => prev.map(p => p.postID === editingCaption.postId ? { ...p, contentText: editCaptionText } : p))
      setEditingCaption(null)
    } catch (err) {
      console.error('[SMM] edit caption failed', err)
      alert(err instanceof Error ? err.message : 'Edit failed')
    }
  }

  const targetStatusIcon = (status: string) => {
    switch (status) {
      case 'Published': return '\u2713'
      case 'Failed':
      case 'DeleteFailed': return '\u2717'
      case 'Deleted': return '\u2716'
      default: return status
    }
  }

  return (
    <div className="smm-posts">
      <div className="smm-posts-header">
        <h1>Post History</h1>
        <Link to="/composer" className="smm-btn smm-btn-primary">New Post</Link>
      </div>

      <div className="smm-filter-bar" style={{ display: 'flex', gap: '0.5rem', flexWrap: 'wrap', alignItems: 'center' }}>
        {STATUS_FILTERS.map(s => (
          <button key={s} onClick={() => setFilter(s)}
            className={`smm-filter-btn ${filter === s ? 'active' : ''}`}>
            {s}
          </button>
        ))}
        <span style={{ borderLeft: '1px solid #ddd', height: 24, margin: '0 0.25rem' }} />
        <MultiSelectDropdown
          options={platforms.map(p => ({ value: p, label: p }))}
          selected={platformFilter}
          onChange={setPlatformFilter}
          placeholder="All Platforms"
          selectAllLabel="Select All Platforms"
        />
        <input type="text" className="smm-input" placeholder="Search content..."
          value={searchText} onChange={e => setSearchText(e.target.value)}
          style={{ maxWidth: 200, height: 32, padding: '0.25rem 0.5rem' }} />
      </div>

      {loading && <p>Loading...</p>}
      {!loading && filteredPosts.length === 0 && (
        <div className="smm-empty-state">
          <p>No posts found. <Link to="/composer">Create your first post</Link></p>
        </div>
      )}

      <div className="smm-posts-list">
        {filteredPosts.map(post => (
          <div key={post.postID} className={`smm-card smm-post-card${post.status === 'Deleted' ? ' smm-post-deleted' : ''}`}>
            <div className="smm-post-meta">
              <span className={`smm-badge smm-badge-${post.status.toLowerCase()}`}>{post.status}</span>
              <span className="smm-post-date">
                {post.scheduledAt
                  ? `Scheduled: ${new Date(post.scheduledAt).toLocaleString()}`
                  : new Date(post.createdAt).toLocaleString()}
              </span>
            </div>
            <p className="smm-post-content">{post.contentText?.slice(0, 200)}{(post.contentText?.length ?? 0) > 200 ? '...' : ''}</p>
            {post.mediaJson && (() => {
              try {
                const media: Array<{ url?: string; blobUrl?: string; mimeType?: string; fileName?: string; _platformMeta?: unknown; _isYouTubeThumbnail?: boolean }> = JSON.parse(post.mediaJson)
                if (!Array.isArray(media) || media.length === 0) return null
                const mediaItems = media
                  .filter(m => !m._platformMeta && !m._isYouTubeThumbnail && (m.url || m.blobUrl))
                  .map(m => {
                    const src = m.blobUrl || m.url || ''
                    const mime = m.mimeType || ''
                    const isVid = mime.startsWith('video') || src.endsWith('.mp4') || src.endsWith('.mov')
                    return { url: src, isVideo: isVid, fileName: m.fileName }
                  })
                if (mediaItems.length === 0) return null
                return (
                  <div style={{ display: 'flex', gap: '0.5rem', marginBottom: '0.5rem', flexWrap: 'wrap' }}>
                    {mediaItems.slice(0, 6).map((m, i) => (
                      <div key={i}
                        onClick={() => openMediaModal(m.url, m.isVideo, i, mediaItems)}
                        style={{
                          width: 80, height: 80, borderRadius: 8, overflow: 'hidden',
                          background: '#1e293b', flexShrink: 0, position: 'relative',
                          cursor: 'pointer', border: '1px solid rgba(255,255,255,0.1)',
                          transition: 'transform 0.15s, box-shadow 0.15s'
                        }}
                        onMouseEnter={e => { e.currentTarget.style.transform = 'scale(1.05)'; e.currentTarget.style.boxShadow = '0 4px 12px rgba(0,0,0,0.3)' }}
                        onMouseLeave={e => { e.currentTarget.style.transform = 'scale(1)'; e.currentTarget.style.boxShadow = 'none' }}>
                        {m.isVideo ? (
                          <>
                            <video src={m.url} muted preload="metadata" style={{ width: '100%', height: '100%', objectFit: 'cover' }}
                              onLoadedMetadata={e => { const v = e.target as HTMLVideoElement; v.currentTime = Math.min(1, v.duration * 0.1) }} />
                            <span style={{ position: 'absolute', inset: 0, display: 'flex', alignItems: 'center', justifyContent: 'center', background: 'rgba(0,0,0,0.3)', color: '#fff', fontSize: '1.2rem' }}>&#9654;</span>
                          </>
                        ) : (
                          <img src={m.url} alt={m.fileName || ''} style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                        )}
                      </div>
                    ))}
                    {mediaItems.length > 6 && <span style={{ alignSelf: 'center', fontSize: '0.85rem', color: '#94a3b8' }}>+{mediaItems.length - 6}</span>}
                  </div>
                )
              } catch { return null }
            })()}
            {post.targets && post.targets.length > 0 && (
              <div className="smm-post-targets">
                {post.targets.map(t => {
                  const isDeleted = t.status === 'Deleted'
                  const isDeleteFailed = t.status === 'DeleteFailed'
                  const canDelete = t.status === 'Published' && !!t.platformPostID
                  const isDeleting = deletingTargets.has(t.postTargetID)
                  const platformUrl = t.status === 'Published' ? getPlatformPostUrl(t.platformName, t.platformPostID, t.displayName) : null
                  const label = t.platformName && t.displayName && t.displayName !== t.platformName
                    ? `${t.platformName} (${t.displayName})`
                    : t.displayName || t.platformName || 'Unknown'
                  return (
                    <span key={t.postTargetID}
                      className={`smm-target-chip${isDeleted ? ' smm-target-deleted' : ''}${isDeleteFailed ? ' smm-target-delete-failed' : ''}`}
                      title={t.errorMessage || (isDeleted ? 'Deleted from platform' : platformUrl ? 'Click to view on platform' : '')}>
                      {platformUrl ? (
                        <a href={platformUrl} target="_blank" rel="noopener noreferrer"
                          style={{ color: 'inherit', textDecoration: 'none' }}
                          onClick={e => e.stopPropagation()}>
                          <span style={isDeleted ? { textDecoration: 'line-through', opacity: 0.6 } : undefined}>
                            {label}
                          </span>
                        </a>
                      ) : (
                        <span style={isDeleted ? { textDecoration: 'line-through', opacity: 0.6 } : undefined}>
                          {label}
                        </span>
                      )}
                      <span className={`smm-chip-status smm-chip-${t.status.toLowerCase()}`}>
                        {targetStatusIcon(t.status)}
                      </span>
                      {canDelete && !isDeleting && (t.platformName === 'Facebook' || t.platformName === 'Instagram') && (
                        <button className="smm-target-edit-btn"
                          onClick={(e) => {
                            e.stopPropagation()
                            setEditingCaption({ postId: post.postID, targetId: t.postTargetID, connectionId: t.connectionID, platformPostID: t.platformPostID!, currentText: post.contentText || '' })
                            setEditCaptionText(post.contentText || '')
                          }}
                          title="Edit caption on platform"
                          style={{ marginLeft: 2, background: 'none', border: 'none', cursor: 'pointer', padding: '0 2px', fontSize: '0.7rem', color: '#1976d2' }}>
                          &#9998;
                        </button>
                      )}
                      {canDelete && !isDeleting && (
                        <button className="smm-target-delete-btn"
                          onClick={(e) => { e.stopPropagation(); handleDeleteFromPlatform(post.postID, t.postTargetID, t.displayName || t.platformName || 'platform') }}
                          title="Delete from platform">
                          &times;
                        </button>
                      )}
                      {isDeleting && <span className="smm-target-deleting">...</span>}
                    </span>
                  )
                })}
              </div>
            )}
            <div className="smm-post-actions">
              {post.status === 'Draft' && (
                <button onClick={() => handlePublish(post.postID)} className="smm-btn smm-btn-sm">Publish</button>
              )}
              <button onClick={() => handleDelete(post.postID)} className="smm-btn smm-btn-sm smm-btn-danger">Remove</button>
            </div>
          </div>
        ))}
      </div>

      {editingCaption && (
        <div style={{ position: 'fixed', inset: 0, zIndex: 9999, background: 'rgba(0,0,0,0.5)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}
          onClick={() => setEditingCaption(null)}>
          <div onClick={e => e.stopPropagation()}
            style={{ background: '#fff', borderRadius: 12, maxWidth: 520, width: '90%', padding: 24, boxShadow: '0 8px 32px rgba(0,0,0,0.2)' }}>
            <h3 style={{ margin: '0 0 12px' }}>Edit Caption</h3>
            <textarea
              value={editCaptionText}
              onChange={e => setEditCaptionText(e.target.value)}
              rows={6}
              style={{ width: '100%', padding: 10, borderRadius: 6, border: '1px solid #ddd', resize: 'vertical', fontFamily: 'inherit', fontSize: '0.9rem' }}
            />
            <div style={{ display: 'flex', gap: 8, marginTop: 12, justifyContent: 'flex-end' }}>
              <button onClick={() => setEditingCaption(null)}
                style={{ padding: '8px 16px', background: '#f5f5f5', border: '1px solid #ddd', borderRadius: 6, cursor: 'pointer' }}>Cancel</button>
              <button onClick={handleEditCaption}
                style={{ padding: '8px 16px', background: '#1976d2', color: '#fff', border: 'none', borderRadius: 6, cursor: 'pointer' }}>Save</button>
            </div>
          </div>
        </div>
      )}

      {modalMedia && (
        <div style={{
          position: 'fixed', inset: 0, zIndex: 9999, background: 'rgba(0,0,0,0.85)',
          display: 'flex', alignItems: 'center', justifyContent: 'center'
        }}
          onClick={() => setModalMedia(null)}>
          <button onClick={(e) => { e.stopPropagation(); setModalMedia(null) }}
            style={{
              position: 'absolute', top: 16, right: 24, background: 'none', border: 'none',
              color: '#fff', fontSize: '2rem', cursor: 'pointer', zIndex: 10
            }}>&times;</button>

          {modalMedia.all.length > 1 && modalMedia.index > 0 && (
            <button onClick={(e) => { e.stopPropagation(); navigateModal(-1) }}
              style={{
                position: 'absolute', left: 24, background: 'rgba(255,255,255,0.15)',
                border: 'none', color: '#fff', fontSize: '2rem', cursor: 'pointer',
                borderRadius: '50%', width: 48, height: 48, display: 'flex', alignItems: 'center', justifyContent: 'center'
              }}>&lsaquo;</button>
          )}

          {modalMedia.all.length > 1 && modalMedia.index < modalMedia.all.length - 1 && (
            <button onClick={(e) => { e.stopPropagation(); navigateModal(1) }}
              style={{
                position: 'absolute', right: 24, background: 'rgba(255,255,255,0.15)',
                border: 'none', color: '#fff', fontSize: '2rem', cursor: 'pointer',
                borderRadius: '50%', width: 48, height: 48, display: 'flex', alignItems: 'center', justifyContent: 'center'
              }}>&rsaquo;</button>
          )}

          <div onClick={(e) => e.stopPropagation()} style={{ maxWidth: '90vw', maxHeight: '85vh' }}>
            {modalMedia.isVideo ? (
              <video src={modalMedia.url} controls autoPlay style={{ maxWidth: '90vw', maxHeight: '85vh', borderRadius: 8 }} />
            ) : (
              <img src={modalMedia.url} alt="" style={{ maxWidth: '90vw', maxHeight: '85vh', borderRadius: 8, objectFit: 'contain' }} />
            )}
          </div>

          {modalMedia.all.length > 1 && (
            <div style={{ position: 'absolute', bottom: 20, color: '#fff', fontSize: '0.9rem' }}>
              {modalMedia.index + 1} / {modalMedia.all.length}
            </div>
          )}
        </div>
      )}
    </div>
  )
}
